import { useState } from 'react';

interface BeaconData {
  id: string;
  beaconNo: string;
  location: string;
  type: string;
  status: string;
  lastCheck: string;
  issue: string;
}

interface ObstacleData {
  id: string;
  location: string;
  type: string;
  severity: string;
  reportTime: string;
  handled: boolean;
}

export default function ChannelInspection() {
  const [beacons] = useState<BeaconData[]>([
    { id: 'B001', beaconNo: '1号航标', location: '主航道入口', type: '灯浮标', status: '正常', lastCheck: '2025-01-15 08:00', issue: '-' },
    { id: 'B002', beaconNo: '2号航标', location: '主航道中段', type: '立标', status: '正常', lastCheck: '2025-01-15 08:15', issue: '-' },
    { id: 'B003', beaconNo: '3号航标', location: '转向区', type: '灯浮标', status: '移位', lastCheck: '2025-01-15 08:30', issue: '偏离原位3米' },
    { id: 'B004', beaconNo: '4号航标', location: '锚地入口', type: '立标', status: '损坏', lastCheck: '2025-01-15 08:45', issue: '灯光故障' },
    { id: 'B005', beaconNo: '5号航标', location: '港池区', type: '浮标', status: '正常', lastCheck: '2025-01-15 09:00', issue: '-' },
  ]);

  const [obstacles] = useState<ObstacleData[]>([
    { id: 'O001', location: '主航道东侧', type: '非法养殖', severity: '高', reportTime: '2025-01-15 07:30', handled: false },
    { id: 'O002', location: '锚地北部', type: '渔网', severity: '中', reportTime: '2025-01-15 08:20', handled: true },
    { id: 'O003', location: '港池入口', type: '漂浮物', severity: '低', reportTime: '2025-01-15 09:10', handled: true },
  ]);

  const normalCount = beacons.filter(b => b.status === '正常').length;
  const abnormalCount = beacons.filter(b => b.status !== '正常').length;
  const unhandledObstacles = obstacles.filter(o => !o.handled).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">航道与航标巡检</h2>
        <button className="px-4 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-flight-takeoff-line mr-2"></i>启动巡检
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">航标总数</span>
            <i className="ri-map-pin-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{beacons.length}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">正常航标</span>
            <i className="ri-checkbox-circle-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-teal-400">{normalCount}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">异常航标</span>
            <i className="ri-error-warning-line text-orange-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-orange-400">{abnormalCount}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">待处理碍航物</span>
            <i className="ri-alert-line text-red-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-red-400">{unhandledObstacles}</div>
          <div className="text-xs text-slate-500 mt-1">处</div>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">航道巡检画面</h3>
        <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
          <img 
            src="https://public.readdy.ai/ai/img_res/b8042861b524e22f4321d0d333842616.jpg"
            alt="航道巡检"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute top-4 left-4 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
            <i className="ri-flight-takeoff-line mr-2"></i>
            巡检中
          </div>
          <div className="absolute bottom-4 left-4 right-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-400">当前位置:</span>
                <span className="text-white ml-2">主航道中段</span>
              </div>
              <div>
                <span className="text-slate-400">飞行高度:</span>
                <span className="text-white ml-2">80m</span>
              </div>
              <div>
                <span className="text-slate-400">巡检进度:</span>
                <span className="text-white ml-2">65%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">航标状态列表</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">航标编号</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">位置</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">类型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">问题</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {beacons.map((beacon) => (
                  <tr key={beacon.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">{beacon.beaconNo}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{beacon.location}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{beacon.type}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        beacon.status === '正常' ? 'bg-teal-500/20 text-teal-400' : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {beacon.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{beacon.issue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">碍航物记录</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">位置</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">类型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">严重程度</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">上报时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">状态</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {obstacles.map((obstacle) => (
                  <tr key={obstacle.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{obstacle.location}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{obstacle.type}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        obstacle.severity === '高' ? 'bg-red-500/20 text-red-400' :
                        obstacle.severity === '中' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {obstacle.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{obstacle.reportTime}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        obstacle.handled ? 'bg-teal-500/20 text-teal-400' : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {obstacle.handled ? '已处理' : '待处理'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
